import { useState, useMemo } from "react";
import { ROOTSTOCKS, ROOTSTOCK_SERIES, VIGOR_ORDER } from "@/lib/data";
import { Reveal, Overline } from "@/components/Reveal";
import { useLang } from "@/i18n";
import PillBadge from "@/components/PillBadge";

const vigorTone = { Dwarfing: "green", "Semi-dwarf": "amber", "Semi-vigorous": "yellow", Vigorous: "red" };

/** Filterable apple-rootstock guide — filter by series and by vigor class. */
const RootstockGuide = () => {
  const [series, setSeries] = useState("All");
  const [vigor, setVigor] = useState("All");
  const { t, data } = useLang();

  const filtered = useMemo(
    () => ROOTSTOCKS.filter((r) => (series === "All" || r.series === series) && (vigor === "All" || r.vigor === vigor)),
    [series, vigor]
  );

  const Chip = ({ label, active, onClick, testid }) => (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-sm font-semibold px-4 py-2 rounded-full border transition-colors duration-200 ${
        active ? "bg-[#1d3a1f] text-[#fdfbf5] border-[#1d3a1f]" : "bg-white text-[#5f6b57] border-[#1d3a1f]/15 hover:border-[#d3a53a] hover:text-[#1d3a1f]"
      }`}
    >
      {label}
    </button>
  );

  return (
    <section id="rootstock-guide" className="py-16 md:py-24 bg-[var(--cream)]">
      <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12">
        <Reveal><Overline>{t("rootstockGuide.overline")}</Overline></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-4 font-display font-bold tracking-tight text-3xl md:text-4xl lg:text-5xl max-w-[20ch]">{t("rootstockGuide.title")}</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-5 text-[#5f6b57] text-base md:text-lg max-w-3xl leading-relaxed">{t("rootstockGuide.intro")}</p>
        </Reveal>

        {/* Filters */}
        <div className="mt-10 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#5f6b57] me-1">{t("rootstockGuide.series")}</span>
            <Chip label={t("rootstockGuide.all")} active={series === "All"} onClick={() => setSeries("All")} testid="filter-series-all" />
            {/* Series names (Malling, Geneva …) are proper nouns — kept in Latin */}
            {ROOTSTOCK_SERIES.map((s) => <Chip key={s} label={s} active={series === s} onClick={() => setSeries(s)} testid={`filter-series-${s.replace(/[^A-Za-z]/g, "").toLowerCase()}`} />)}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#5f6b57] me-1">{t("rootstockGuide.vigor")}</span>
            <Chip label={t("rootstockGuide.all")} active={vigor === "All"} onClick={() => setVigor("All")} testid="filter-vigor-all" />
            {VIGOR_ORDER.map((vg) => <Chip key={vg} label={data.vigor?.[vg] || vg} active={vigor === vg} onClick={() => setVigor(vg)} testid={`filter-vigor-${vg.replace(/[^A-Za-z]/g, "").toLowerCase()}`} />)}
          </div>
        </div>

        {/* Grid */}
        <div data-testid="rootstock-grid" className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((r) => {
            const dr = data.rootstocks?.[r.name];
            return (
              <article key={r.name} data-testid={`rootstock-${r.name.replace(/[^A-Za-z0-9]/g, "").toLowerCase()}`}
                className="h-full bg-white border border-[#1d3a1f]/10 rounded-2xl p-6 card-soft hover:border-[#d3a53a] hover:-translate-y-1.5 hover:shadow-xl transition-all duration-300">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-display font-extrabold text-2xl text-[#1d3a1f]">{r.name}</h3>
                  <PillBadge tone={vigorTone[r.vigor]}>{data.vigor?.[r.vigor] || r.vigor}</PillBadge>
                </div>
                <span className="mt-1 block text-xs font-semibold uppercase tracking-[0.12em] text-[#d3a53a]">{r.series}</span>
                <p className="mt-4 text-sm text-[#5f6b57] leading-relaxed">{dr?.traits || r.traits}</p>
                <div className="mt-4 pt-4 border-t border-[#1d3a1f]/10">
                  <span className="text-xs font-bold uppercase tracking-[0.12em] text-[#1d3a1f]">{t("rootstockGuide.bestFit")}</span>
                  <p className="mt-1 text-sm text-[#5f6b57]">{dr?.fit || r.fit}</p>
                </div>
              </article>
            );
          })}
        </div>
        {filtered.length === 0 && <p className="mt-8 text-[#5f6b57]">{t("rootstockGuide.noMatch")}</p>}
      </div>
    </section>
  );
};

export default RootstockGuide;
